export type CookiePolicySubsection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type CookiePolicySection = {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subsections?: CookiePolicySubsection[];
};

export const COOKIE_POLICY_CONTENT = {
  title: 'VEVI Monster Cookie Policy',
  lastUpdated: 'Last updated: June 5, 2026',
  seoTitle: 'Cookie Policy | VEVI Monster',
  seoDescription:
    'Learn how VEVI Monster uses cookies and similar technologies for essential site functions, analytics, language preferences, and support tools.',
  backToHomeLabel: 'Back to Home',
  controller: {
    heading: '1. Introduction',
    intro:
      'This Cookie Policy explains how VEVI Monster uses cookies and similar technologies on vevi.monster, including our AI dubbing, captions, audio translation, upload, checkout, and support flows. It should be read together with our Privacy Policy.',
    companyName: 'General Secret OÜ',
    companyDetails:
      'Registry code 17112803, Vesivärava tn 50-201, Kesklinna linnaosa, Tallinn, Harju maakond, 10152, Estonia',
    email: 'site@vevi.monster',
  },
  sections: [
    {
      title: '2. What Cookies Are',
      paragraphs: [
        'Cookies are small text files stored on your browser or device when you visit a website. They can help a site remember settings, keep core functionality working, understand how visitors use pages, and support tools such as chat widgets or embedded services.',
        'We may also use similar technologies such as local storage, browser identifiers, and scripts that serve a similar purpose when they are needed to operate or measure the VEVI Monster experience.',
      ],
    },
    {
      title: '3. Types of Cookies We Use',
      bullets: [
        'Essential cookies needed for core website and service functionality',
        'Analytics cookies used to understand traffic, usage patterns, and page performance',
        'Functional cookies used to remember preferences such as language or interface state',
        'Third-party cookies that may be set by providers such as analytics, support, or payment tools',
      ],
      paragraphs: [
        'We do not currently rely on advertising cookies for VEVI Monster marketing pages.',
      ],
    },
    {
      title: '4. Essential Cookies',
      paragraphs: [
        'Essential cookies help vevi.monster function safely and correctly. Without them, important parts of the website or service flow may not work as expected.',
      ],
      bullets: [
        'Maintaining secure website sessions and basic security protections',
        'Supporting upload, checkout, and media-processing flows',
        'Preserving temporary service state while you move between steps',
        'Helping prevent abuse, spam, or repeated technical failures',
      ],
    },
    {
      title: '5. Analytics Cookies',
      paragraphs: [
        'We use Google Analytics 4 to understand how visitors arrive on vevi.monster, which pages they use, what devices they use, and how the website performs at a high level.',
        'Analytics cookies may collect information such as browser type, approximate location, referrer data, page views, session timing, and interaction events. We use this information in aggregated form to improve product pages, upload flows, and site performance.',
      ],
    },
    {
      title: '6. Functional Cookies',
      paragraphs: [
        'Functional cookies help remember choices and preferences that make the website easier to use.',
      ],
      bullets: [
        'Remembering preferred language or locale-related interface behavior',
        'Supporting support-chat state or user assistance tools',
        'Preserving non-sensitive interface settings during a visit',
      ],
    },
    {
      title: '7. Third-Party Cookies',
      paragraphs: [
        'Some cookies may be placed by third-party providers that help us operate VEVI Monster. These providers may process data under their own privacy and cookie terms.',
      ],
      bullets: [
        'Google Analytics 4 for traffic and usage analytics',
        'Customer support or chat tooling used to respond to questions',
        'Payment providers such as Revolut when you enter or complete a checkout flow',
      ],
      subsections: [
        {
          title: 'Important note',
          paragraphs: [
            'When you interact with third-party tools or leave vevi.monster for a hosted payment or embedded service experience, those providers may set their own cookies independently of VEVI Monster.',
          ],
        },
      ],
    },
    {
      title: '8. Cookie Management',
      paragraphs: [
        'You can control or remove cookies through your browser settings. Most browsers let you review stored cookies, delete them, block specific cookies, or block all cookies entirely.',
        'If you disable some cookies, parts of VEVI Monster may become less reliable. For example, language handling, support tools, analytics measurement, or upload-related flows may not behave as intended.',
      ],
    },
    {
      title: '9. Browser Settings',
      paragraphs: [
        'Browser controls are usually available in the privacy, security, or site-settings section of your browser. The exact steps depend on the browser and device you use.',
        'If you want broader control, you can also look for device-level privacy settings or use browser extensions that manage cookies and site data.',
      ],
    },
    {
      title: '10. Changes to This Policy',
      paragraphs: [
        'We may update this Cookie Policy from time to time to reflect changes to VEVI Monster, our providers, or legal requirements. When we make updates, we will revise the date shown at the top of this page.',
      ],
    },
    {
      title: '11. Contact Information',
      paragraphs: [
        'If you have questions about this Cookie Policy or our use of cookies and similar technologies, contact us at site@vevi.monster.',
      ],
    },
  ] satisfies CookiePolicySection[],
} as const;
